import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * רושם שהמסך הזה נפתח.
 *
 * אלי, 10/09/2026, על מסכי הייצור: **„תציע לי איזה מסכים למחוק כי הם
 * משוכפלים... או כי הם סתם מיותרים.“** ובמערכת לא הייתה שום מדידת שימוש,
 * ולכן על השאלה „מי פותח מה“ לא הייתה שום תשובה. הוא בחר: „להוסיף מדידה
 * עכשיו, להחליט בעוד שבועיים.“
 *
 * ── מה זה **לא** עושה ────────────────────────────────────────────────────
 *
 * לא ממתין לתשובה, לא מציג שגיאה, ולא נכשל: מדידה שמפילה ניווט היא מדידה
 * שתכובה ביום הראשון. `keepalive` מבטיח שהבקשה יוצאת גם כשהמשתמש עובר מסך
 * באותו רגע — בלעדיו הכניסה האחרונה לפני יציאה נעלמת דווקא במסך שנפתח
 * לרגע, וזה בדיוק המסך שמעניין לספור.
 *
 * ── ופעם אחת לכל מסך ────────────────────────────────────────────────────
 *
 * `useEffect` על `pathname` בלבד: רינדור מחדש בגלל נתון שהתעדכן אינו כניסה
 * חדשה, ובלי זה מסך שמרענן כל 30 שניות היה נראה כמסך הנצפה ביותר בחברה.
 */
/**
 * „פתחת לאחרונה" בחלון החיפוש (תפריט v6, אלי 06/10/2026). בדפדפן ולא בשרת:
 * זו נוחות של המכשיר הזה, ולא עולה כלום לבנות מחדש.
 */
export const RECENT_KEY = 'edal.nav.recent';

export function readRecent(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function pushRecent(pathname: string): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([pathname, ...readRecent().filter((p) => p !== pathname)].slice(0, 8)));
  } catch { /* חלון פרטי */ }
}

/**
 * „אחרונים" של הדברים עצמם — פרויקט, קריאה, ספק… שנפתחו מחלון החיפוש
 * (V1, אלי 30/10/2026). אותו כלל כמו המסכים: נוחות של המכשיר, 8 אחרונים.
 * שומרים רק מה שצריך כדי לצייר שורה ולפתוח אותה — לא מצב, שמתיישן.
 */
export type RecentFind = { type: string; id: string; code: string | null; title: string; sub: string | null; to: string };
export const RECENT_FIND_KEY = 'edal.find.recent';

export function readRecentFinds(): RecentFind[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(RECENT_FIND_KEY) ?? '[]');
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is RecentFind => !!x && typeof x === 'object'
      && typeof (x as RecentFind).id === 'string' && typeof (x as RecentFind).to === 'string' && typeof (x as RecentFind).title === 'string');
  } catch {
    return [];
  }
}

export function pushRecentFind(it: RecentFind): void {
  try {
    const row: RecentFind = { type: it.type, id: it.id, code: it.code, title: it.title, sub: it.sub, to: it.to };
    const rest = readRecentFinds().filter((r) => !(r.type === it.type && r.id === it.id));
    localStorage.setItem(RECENT_FIND_KEY, JSON.stringify([row, ...rest].slice(0, 8)));
  } catch { /* חלון פרטי */ }
}

export function useScreenVisit(): void {
  const { pathname } = useLocation();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (last.current === pathname) return;
    last.current = pathname;
    // ⚠ הנתיב של דף ה-QR **הוא** האסימון (01/11/2026). בלי השורה הזו הוא היה
    // נשמר בטבלת המדידה ובאחרונים של המכשיר.
    if (pathname.startsWith('/photo/')) return;
    pushRecent(pathname);
    // ‏void: אין למי לחכות, ואין מה לעשות אם זה נכשל.
    void fetch('/api/usage/visit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ path: pathname }),
      credentials: 'include',
      keepalive: true,
    }).catch(() => { /* מדידה שנכשלה אינה אירוע */ });
  }, [pathname]);
}
